/**
 * Asset Files section attached to the Asset form via `FormExtras`.
 *
 * Persists each upload to the `/vehicle-files` collection (legacy schema
 * name; UI label is "Asset Files"). Two-step write — same pattern the
 * JobCard Documents uploader uses:
 *   1. POST /uploads (multipart) with `prefix: 'vehicle-files'` → S3 key
 *   2. POST /vehicle-files with { asset, fileName, fileKey, fileUpload }
 *
 * The "File Name" input is what the operator wants to label the file as
 * on Atlas — defaults to the original filename if blank. Stored on
 * `VehicleFile.fileName`; the S3 object key lives on `fileKey`.
 *
 * Create mode: shows a hint instead — there's no asset id to attach
 * uploads to until the record is saved.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Trash2, Plus, Loader2 } from 'lucide-react';
import { api, apiUpload } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import type { FormExtrasContext } from '@/components/shared/crud-page';

interface FileRow {
  id: string;
  fileName: string | null;
  fileUpload: string | null;
  fileKey: string | null;
}

/** Standalone "Open" link — fetches a pre-signed S3 URL for the given key
 *  and renders an anchor that opens in a new tab. Used both by this
 *  subform's row display and by the Asset detail panel's Files related
 *  list (which is just a list of columns, so it needs a renderable cell). */
export function FileDownloadLink({ fileKey }: { fileKey: string | null }) {
  const { data } = useQuery({
    queryKey: ['upload-url', fileKey],
    queryFn: async () => {
      if (!fileKey) return null;
      const r = await api<{ key: string; url: string }>(`/uploads/${encodeURIComponent(fileKey)}/url`);
      return r.data ?? null;
    },
    enabled: !!fileKey,
    staleTime: 30 * 60 * 1000,
  });
  if (!fileKey || !data?.url) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <a
      href={data.url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="text-xs text-primary hover:underline"
    >
      Open
    </a>
  );
}

export function AssetFilesSubform({ isEdit, parentId }: FormExtrasContext) {
  return (
    <div className="border-t pt-4 mt-2 space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground border-b pb-2">
        Asset Files
      </h3>
      {!isEdit || !parentId ? (
        <p className="text-xs text-muted-foreground py-2">
          Save the asset first — files can be attached after the record exists.
        </p>
      ) : (
        <AssetFilesList assetId={parentId} />
      )}
    </div>
  );
}

function AssetFilesList({ assetId }: { assetId: string }) {
  const qc = useQueryClient();
  const queryKey = ['vehicle-files', assetId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await api<FileRow[]>(`/vehicle-files?asset=${assetId}&limit=200`);
      return r.data ?? [];
    },
    enabled: !!assetId,
  });
  const rows = data ?? [];

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/vehicle-files/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Delete failed');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast('Removed', undefined, 'success');
    },
    onError: (e: any) => toast('Error', e?.message ?? String(e), 'error'),
  });

  return (
    <div className="space-y-3">
      {isLoading && <div className="text-sm text-muted-foreground py-2">Loading…</div>}
      {!isLoading && rows.length === 0 && (
        <div className="text-sm text-muted-foreground py-2">No files uploaded yet.</div>
      )}
      {rows.map((row) => (
        <FileRowDisplay key={row.id} row={row} onRemove={() => remove.mutate(row.id)} />
      ))}
      <AddFileForm assetId={assetId} onAdded={() => qc.invalidateQueries({ queryKey })} />
    </div>
  );
}

function FileRowDisplay({ row, onRemove }: { row: FileRow; onRemove: () => void }) {
  const key = row.fileKey;
  // Strip the upload timestamp prefix from S3-key-derived filenames so the
  // download UI shows "service-manual.pdf" instead of "1737800000-service-manual.pdf".
  const filename = key ? key.split('/').pop()!.replace(/^\d+-/, '') : (row.fileUpload || row.fileName || 'Untitled');

  return (
    <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
      <div className="h-12 w-12 rounded border flex items-center justify-center bg-background shrink-0">
        <FileText className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate" title={row.fileName || filename}>
          {row.fileName || filename}
        </div>
        <div className="text-xs text-muted-foreground truncate">{filename}</div>
        <FileDownloadLink fileKey={key} />
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="text-destructive hover:text-destructive/80"
        aria-label="Remove file"
        title="Remove"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function AddFileForm({ assetId, onAdded }: { assetId: string; onAdded: () => void }) {
  const [name, setName] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function handleFile(file: File) {
    setBusy(true);
    try {
      // 1. Upload binary → server returns the S3 object key.
      const fd = new FormData();
      fd.append('prefix', 'vehicle-files');
      fd.append('file', file);
      const up = await apiUpload<{ key: string; url: string }>('/uploads', fd);
      if (!up.success || !up.data?.key) throw new Error(up.error?.message || 'Upload failed');

      // 2. Persist the metadata row. `fileUpload` retains the original
      //    filename (legacy column the Zoho backfill writes to); `fileKey`
      //    holds the S3 object key the download URL is signed against.
      const body = {
        asset: assetId,
        fileName: name.trim() || file.name,
        fileUpload: file.name,
        fileKey: up.data.key,
      };
      const created = await api('/vehicle-files', { method: 'POST', body: JSON.stringify(body) });
      if (!created.success) throw new Error(created.error?.message || 'Save failed');

      setName('');
      onAdded();
      toast('Uploaded', undefined, 'success');
    } catch (e: any) {
      toast('Error', e?.message ?? String(e), 'error');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="border-t pt-3 mt-2 space-y-2">
      <div>
        <Label className="text-xs">File name (optional)</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Defaults to the filename"
          className="h-9"
        />
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
        {busy ? 'Uploading…' : 'Add file'}
      </Button>
    </div>
  );
}
